/**
 * DesktopProfilePanel - Desktop-only profile panel wrapper
 * Extracted from App.tsx for modularization
 */
import React, { useMemo } from 'react';
import ProfilePanel, { 
  type ProfileDiff, 
  type ProfileConflict, 
  type RecentChange,
  type EditorPick,
  type FirebaseUser,
  type CartItem
} from './ProfilePanel';
import type { MasterProfile } from '../../types/profile';
import type { WishlistItem } from '../../types/product';
import type { RecentChanges } from '../../types/conversation';

interface DesktopProfilePanelProps {
  masterProfile: Partial<MasterProfile>;
  recentChanges?: RecentChanges | null;
  profileDiff?: unknown;
  profileConflicts?: ProfileConflict[];
  coppaBlocked: boolean;
  isProfileLoading: boolean;
  profileError: string | null;
  onUndo: (path: string) => void;
  onUpdateProfile: (profile: MasterProfile) => void;
  editorPicks?: EditorPick[];
  isEditorPicksLoading: boolean;
  /** Whether editor picks are from affiliate sources (Amazon) */
  isEditorPicksAffiliate?: boolean;
  onRefreshEditorPicks: () => void;
  autoSave: boolean;
  language: string;
  showAddressFormForSample: boolean;
  onCancelAddressForm: () => void;
  user?: FirebaseUser | null;
  cartItems?: CartItem[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onAddToCart?: (item: any) => void | Promise<void>;
  onPhotoUpload: (file: File) => void;
  // Wishlist props
  wishlistItems?: WishlistItem[];
  onWishlistToggle?: (product: EditorPick) => void;
  isInWishlist?: (productId: string) => boolean;
  onViewDetails?: (pick: EditorPick) => void;
  onAskAI?: (productName: string) => void;
}

export default function DesktopProfilePanel({
  masterProfile,
  recentChanges,
  profileDiff,
  profileConflicts,
  coppaBlocked,
  isProfileLoading,
  profileError,
  onUndo,
  onUpdateProfile,
  editorPicks,
  isEditorPicksLoading,
  isEditorPicksAffiliate,
  onRefreshEditorPicks,
  autoSave,
  language,
  showAddressFormForSample,
  onCancelAddressForm,
  user,
  cartItems,
  onAddToCart,
  onPhotoUpload,
  wishlistItems,
  onWishlistToggle,
  isInWishlist,
  onViewDetails,
  onAskAI,
}: DesktopProfilePanelProps) {
  // Convert App.tsx recentChanges format to ProfilePanel RecentChange[] format
  const formattedRecentChanges: RecentChange[] | undefined = useMemo(() => {
    if (!recentChanges?.diff) return undefined;
    
    return Object.entries(recentChanges.diff).map(([path, change]) => ({
      path,
      newVal: change.new,
    }));
  }, [recentChanges]);

  return (
    <div className="desktop-profile-wrapper desktop-only">
      <ProfilePanel
          masterProfile={masterProfile}
          recentChanges={formattedRecentChanges}
          diff={profileDiff as ProfileDiff | undefined}
          conflicts={profileConflicts}
          coppaBlocked={coppaBlocked}
          isExtracting={isProfileLoading}
          profileError={profileError}
          onUndo={onUndo}
          onUpdateProfile={onUpdateProfile}
          editorPicks={editorPicks}
          isEditorPicksLoading={isEditorPicksLoading}
          isEditorPicksAffiliate={isEditorPicksAffiliate}
          onRefreshEditorPicks={onRefreshEditorPicks}
          autoSave={autoSave}
          language={language}
          showAddressFormForSample={showAddressFormForSample}
          onCancelAddressForm={onCancelAddressForm}
          user={user}
          cartItems={cartItems}
          onAddToCart={onAddToCart as (item: CartItem) => void}
          onPhotoUpload={onPhotoUpload}
          wishlistItems={wishlistItems}
          onWishlistToggle={onWishlistToggle}
          isInWishlist={isInWishlist}
          onViewDetails={onViewDetails}
          onAskAI={onAskAI}
        />
    </div>
  );
}
